import { useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { OneAlert } from '@factorialco/f0-react/dist/experimental'
import { Archive } from '@factorialco/f0-react/icons/app'
import {
  IntegrationsJoinCreateOrderDocument,
  JoinPremiumBundleFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CheckoutModal } from 'modules/ats/pages/JobPostingMarketplace/CheckoutModal'
import { CheckoutProductDetail } from 'modules/ats/pages/JobPostingMarketplace/CheckoutModal/CheckoutProductDetail'

interface Props {
  bundle: JoinPremiumBundleFragment
  onClose: () => void
  isOpen: boolean
}

export const BundleCheckoutModal = ({ bundle, onClose, isOpen }: Props) => {
  const { jobId } = useParams()
  const { company, access } = useCurrentSession()
  const [createOrder, { loading: creatingOrder }] = useMutation(
    IntegrationsJoinCreateOrderDocument,
    {
      extractorFn: (response) => response.integrationsJoinMutations.createIntegrationsjoinOrder,
    }
  )

  const hasDiscount = bundle.pricing.reducedPercentage > 0
  const bundlePrice = hasDiscount ? bundle.pricing.reducedAmount : bundle.pricing.originalAmount

  const buildBundleSnapshotProducts = () => {
    return bundle.products.map((product) => ({
      description: product.description,
      durationDays: product.durationDays,
      jobBoard: {
        slug: product.jobBoard.slug,
        name: product.jobBoard.name,
        logoUrl: product.jobBoard.logoUrl,
      },
    }))
  }

  const handleCreateOrder = async (requesterNote: string | undefined) => {
    const response = await createOrder({
      variables: {
        accessId: access.id,
        atsJobPostingId: Number(jobId),
        premiumOfferId: bundle.id,
        premiumOfferName: bundle.name,
        premiumOfferPrice: bundlePrice,
        premiumOfferType: 'bundle',
        note: requesterNote,
        snapshot: {
          price: bundlePrice,
          currency: bundle.pricing.currencyCode,
          products: buildBundleSnapshotProducts(),
        },
      },
    })

    const handled = await handleResult(response, (error) => {
      app.setFlash({
        type: 'wrong',
        message: error.messages || i18n.t('common.save_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      track(trackingEvents.ats.premiumOfferPurchased, {
        company_id: company.id,
        offer_id: bundle.id,
        offer_name: bundle.name,
        offer_type: 'bundle',
        offer_product_name: bundle.products.map((p) => p.name).join(', '),
        price: bundlePrice,
      })
      onClose()
    }

    return handled
  }

  return (
    <CheckoutModal
      pricing={bundle.pricing}
      isOpen={isOpen}
      onClose={onClose}
      isSubmitting={creatingOrder}
      onSubmit={(requesterNote) => handleCreateOrder(requesterNote)}
    >
      <Box flexDirection="column" gap="s12">
        <Text.Normal size="400" weight="semibold" color="grey1000">
          {bundle.name}
        </Text.Normal>
        <OneAlert
          variant="neutral"
          title=""
          description={`${bundle.products.length} products included`}
          icon={Archive}
        />
        <Box overflowY="auto" maxHeight="s200" gap="s12">
          {bundle.products.map((product) => (
            <CheckoutProductDetail
              key={`modal-bundle-product-${product.id}`}
              offerName={product.name}
              durationDays={product.durationDays}
              jobBoard={product.jobBoard}
            />
          ))}
        </Box>
      </Box>
    </CheckoutModal>
  )
}
